import Button from "@syntensor/common/components/button";
import getCopy from "@syntensor/common/data/copy";
import Loader from "@syntensor/common/components/loader";
import { IProject } from "@syntensor/common/types";
import {
  BookmarksIcon,
  EIconSize,
  TrashIcon,
} from "@syntensor/common/components/icons";

import {
  IsUserInAdminGroup,
  IsUserInGroupAdminGroup,
} from "../auth/auth_context";

import styles from "./project_box.module.css";
import { useMemo, useState, useEffect } from "react";
import { deleteProject } from "@syntensor/common/data/fetch_data";
import ReactMarkdown from "react-markdown";
import { navigateWithQueryParams } from "@syntensor/common/browser_history";

export default function ProjectBox({
  id,
  name,
  description,
  studies,
  onDelete = null,
  onClone = null,
  groupId,
}: IProject) {
  const isUserInAdminGroup = IsUserInAdminGroup();
  const [isUserInGroupAdminGroup, setIsUserInGroupAdminGroup] =
    useState(isUserInAdminGroup);
  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);

  useEffect(() => {
    async function handler() {
      const admin = await IsUserInGroupAdminGroup(groupId ?? 0);
      setIsUserInGroupAdminGroup(admin);
    }

    if (!isUserInAdminGroup && groupId) {
      handler();
    }
  }, [groupId]);

  const projectsCount = useMemo(() => {
    if (studies && studies.length === 1) {
      return `${studies.length} ${getCopy("projects_project_footer")}`;
    } else {
      return `${studies.length} ${getCopy("projects_project_puralFooter")}`;
    }
  }, []);

  const onDeleteClick = async () => {
    if (!isDeleteInProgress) {
      const confirmed = window.confirm(
        getCopy("projects_project_delete-confirm", { title: name })
      );
      if (confirmed) {
        setIsDeleteInProgress(true);
        await deleteProject(id);

        if (onDelete) {
          await onDelete();
          setIsDeleteInProgress(false);
        }
      }
    }
  };

  const onCloneClick = () => {
    if (onClone) {
      navigateWithQueryParams(`/projects/${groupId}/create/${id}`);
    }
  };

  return (
    <div className={styles.studyBox} data-testid="projects_project_study-box">
      {(isUserInAdminGroup || isUserInGroupAdminGroup) &&
        process.env.REACT_APP_PROJECT_UI === "1" && (
          <div className={styles.topBtns}>
            {onClone && (
              <button
                className={styles.topBtn}
                onClick={onCloneClick}
                title={getCopy("projects_project_clone")}
              >
                <BookmarksIcon size={EIconSize.SMALL} />
              </button>
            )}
            {onDelete && (
              <button
                className={styles.topBtn}
                onClick={onDeleteClick}
                disabled={isDeleteInProgress}
                title={getCopy("projects_project_delete")}
              >
                {isDeleteInProgress && <Loader size="18px" />}
                {!isDeleteInProgress && <TrashIcon size={EIconSize.SMALL} />}
              </button>
            )}
          </div>
        )}
      <div className={styles.header}>
        <h3 className={styles.title}>{name}</h3>
      </div>
      <div className={styles.description}>
        {description && <ReactMarkdown>{description}</ReactMarkdown>}
      </div>
      <div className={styles.footer}>
        <div>
          <p>{projectsCount}</p>
        </div>
        <div className={styles.footerBtns}>
          {(isUserInAdminGroup || isUserInGroupAdminGroup) &&
            process.env.REACT_APP_PROJECT_UI === "1" && (
              <Button
                to={`/projects/${id}/edit`}
                role="subtle"
                data-testid="projects_project_study-configure"
              >
                {getCopy("projects_project_configure")}
              </Button>
            )}
          <Button
            to={`/projects/${id}/studies`}
            role="primary"
            data-testid="projects_project_study-open"
          >
            {getCopy("projects_project_open")}
          </Button>
        </div>
      </div>
    </div>
  );
}
